import { useEffect, useState } from "react";
import RenewModal from "./RenewModal";
import UpgradeModal from "./UpgradeModal";
import { toast } from "react-toastify";
import { Breadcrumbs } from "../../../AbstractElements";
import SubscriptionCards from "./SubscriptionCards";
import CurrentSubscription from "./CurrentSubscription";
import {
    confirmPayment,
    getCurrentSubscription,
    RenewActiveSubscription,
    UpgradetoHigherPlan,
} from "../../../api-handle/merchantSubscriptionApi";
import { merchantProfile } from "../../../api-handle/authApi";
import { useLocation, useNavigate } from "react-router";
import { FileText } from "lucide-react";
import "./CurrentSubscription.scss";

function Subscriptions() {
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [renewModalOpen, setRenewModalOpen] = useState(false);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [merchantStatus, setMerchantStatus] = useState(null);
    const [loadingMerchantStatus, setLoadingMerchantStatus] = useState(true);
    const [verifyingPayment, setVerifyingPayment] = useState(
        () => new URLSearchParams(window.location.search).has("paymentId")
    );

    const GetCurrentSubscription = async () => {
        try {
            const res = await getCurrentSubscription();
            setCurrentSubscription(res.subscription || null); // Handle null response
        } catch (error) {
            console.error("Error fetching subscription:", error.message);
            setCurrentSubscription(null);
            // Don't show error toast for pending merchants - these are expected scenarios
            const errorMessage = error?.message || error?.error?.message || '';
            const isExpectedError = 
                errorMessage.includes('Merchant not approved') ||
                errorMessage.includes('not approved') ||
                error?.statusCode === 401;
            
            if (!isExpectedError) {
                toast.error("Failed to fetch subscription");
            }
        }
    };

    const handleRenewPlan = () => {
        if (!currentSubscription?.subscriptionPlan?._id) {
            toast.error("No valid subscription plan to renew");
            return;
        }
        setRenewModalOpen(true);
    };

    const confirmRenew = async () => {
        try {
            await RenewActiveSubscription({ PlanId: currentSubscription.subscriptionPlan._id });
            toast.success("Plan renewed successfully");
            setRenewModalOpen(false);
            GetCurrentSubscription(); // Refetch to update UI
        } catch (error) {
            console.error("Error renewing plan:", error.message);
            toast.error("Failed to renew plan");
        }
    };

    const handleUpgradePlan = (plan) => {
        if (!plan?._id) {
            toast.error("Invalid plan selected for upgrade");
            return;
        }
        setSelectedPlan(plan);
        setUpgradeModalOpen(true);
    };

    const confirmUpgrade = async (planId) => {
        try {
            await UpgradetoHigherPlan({ planId });
            toast.success("Plan upgraded successfully");
            setUpgradeModalOpen(false);
            setSelectedPlan(null); // Reset selected plan
            GetCurrentSubscription(); // Refetch to update UI
        } catch (error) {
            console.error("Error upgrading plan:", error.message);
            toast.error("Failed to upgrade plan");
        }
    };

    useEffect(() => {
        GetCurrentSubscription();
    }, []);

    // Fetch merchant status to check if pending
    useEffect(() => {
        const fetchMerchantStatus = async () => {
            try {
                setLoadingMerchantStatus(true);
                const res = await merchantProfile();
                if (res?.isSuccess && res?.user?.merchant) {
                    setMerchantStatus(res.user.merchant.status);
                }
            } catch (error) {
                console.error("Error fetching merchant status:", error);
                // If error fetching, assume not pending to show normal subscription page
                setMerchantStatus(null);
            } finally {
                setLoadingMerchantStatus(false);
            }
        };
        fetchMerchantStatus();
    }, []);

    const location = useLocation();
    const navigate = useNavigate();

    const checkPaymentStatus = async () => {
        const invoiceId = localStorage.getItem("invoiceId");
        if (!invoiceId) {
            toast.error("Payment reference not found. Please contact support.");
            // Clear the gate so the page renders instead of hanging on the spinner
            // (verifyingPayment was initialized true because ?paymentId is present).
            setVerifyingPayment(false);
            return;
        }
        setVerifyingPayment(true);
        try {
            const res = await confirmPayment({ invoiceId });
            if (res.isSuccess) {
                toast.success(res?.message || "Subscription activated successfully!");
                localStorage.removeItem("invoiceId");
                GetCurrentSubscription();
            } else {
                toast.error(res?.message || "Payment verification failed.");
            }
        } catch (error) {
            toast.error(error?.message || "Payment verification failed.");
        } finally {
            setVerifyingPayment(false);
        }
    };

    // Fatoorah redirects back here with ?paymentId=... after payment (success or failure)
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const paymentId = queryParams.get("paymentId");

        if (!paymentId) return;

        // Clean URL immediately so refresh doesn't re-trigger
        queryParams.delete("paymentId");
        navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });

        checkPaymentStatus();
    }, [location.search]);

    if (verifyingPayment) {
        return (
            <>
                <Breadcrumbs
                    mainTitle="My Subscription"
                    parent="Subscription"
                    title="My Subscription"
                />
                <div style={{ textAlign: "center", padding: "60px 20px" }}>
                    <div className="spinner-border text-primary mb-3" role="status" />
                    <p style={{ fontSize: "1.1rem", color: "#555" }}>Verifying your payment, please wait...</p>
                </div>
            </>
        );
    }

    // Show review banner if merchant is pending
    if (loadingMerchantStatus) {
        return (
            <>
                <Breadcrumbs
                    mainTitle="My Subscription"
                    parent="Subscription"
                    title="My Subscription"
                />
                <div>Loading...</div>
            </>
        );
    }

    if (merchantStatus === 'pending') {
        return (
            <>
                <Breadcrumbs
                    mainTitle="My Subscription"
                    parent="Subscription"
                    title="My Subscription"
                />
                <div className="current-subscription-container" style={{ margin: "20px" }}>
                    <div className="subscription-header">
                        <h2>Subscription</h2>
                    </div>
                    <div className="subscription-details">
                        <div className="detail-card">
                            <div className="icon-wrapper">
                                <FileText size={24} />
                            </div>
                            <div className="detail-content">
                                <span>Account Under Review</span>
                                <p>Your account is under review. Once the review is done, then you can purchase a subscription to access the dashboard.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Breadcrumbs
                mainTitle="My Subscription"
                parent="Subscription"
                title="My Subscription"
            />
            <div>
                <CurrentSubscription
                    subscription={currentSubscription}
                    onRenew={handleRenewPlan}
                    onUpgrade={handleUpgradePlan} // Added for consistency
                />
                <SubscriptionCards
                    onUpgrade={handleUpgradePlan}
                    currentPlanId={currentSubscription?.subscriptionPlan?._id}
                    currentPlan={currentSubscription}
                    currentPlanPrice={currentSubscription?.price}
                />
            </div>

            {/* Renew Modal */}
            <RenewModal
                isOpen={renewModalOpen}
                toggle={() => setRenewModalOpen(false)}
                onConfirm={confirmRenew}
                currentPlan={currentSubscription?.subscriptionPlan}
            />

            {/* Upgrade Modal */}
            <UpgradeModal
                isOpen={upgradeModalOpen}
                toggle={() => setUpgradeModalOpen(false)}
                currentSubscription={currentSubscription}
                selectedPlan={selectedPlan}
                onUpgradeConfirm={confirmUpgrade}
            />
        </>
    );
}

export default Subscriptions;